import React, { useState, useMemo } from 'react';
import { Card, Form, Input, Button, Alert, Typography } from 'antd';
import { UserOutlined, ExclamationCircleOutlined, MailOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import AuthLayout from '../../layouts/AuthLayout';
import logo from '../../assets/logo-login.png';
import { useAuth } from '../../hooks/useAuth';
import { message } from 'antd';

const { Text, Title } = Typography;

export default function ForgotPasswordPage() {
    const [loading, setLoading] = useState(false);
    const [globalError, setGlobalError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { forgot, } = useAuth();

    const mockEnabled = useMemo(
        () => (process.env.REACT_APP_ENABLE_MOCK_AUTH ?? 'true') !== 'false',
        []
    );

    const handleInputChange = () => {
        if (globalError) setGlobalError(null);
    };

    const onFinish = async (values) => {
        setLoading(true);
        setGlobalError(null);

        try {
            const response = await forgot({
                email: values.email,
            });
            message.success(response);
            setSuccess(true);
        } catch (err) {
            console.error('Forgot password error:', err);

            const status = err?.response?.status;
            const data = err?.response?.data;
            const errorMessage = data?.message || err?.message;

            if (status === 400 || status === 422) {
                const fieldErrors = data?.fieldErrors || {};
                const fields = Object.entries(fieldErrors).map(([name, errors]) => ({
                    name,
                    errors: Array.isArray(errors) ? errors : [String(errors)],
                }));

                if (fields.length) {
                    form.setFields(fields);
                } else {
                    setGlobalError('El correo ingresado no es válido.');
                }
            } else if (status === 429) {
                setGlobalError('Demasiadas solicitudes. Intenta nuevamente más tarde.');
            } else if (!navigator.onLine) {
                setGlobalError('Sin conexión a internet. Verifica tu conexión.');
            } else {
                setGlobalError(
                    errorMessage === 'Unknown error'
                        ? 'Ocurrió un error inesperado. Inténtalo más tarde.'
                        : errorMessage
                );
            }
        } finally {
            setLoading(false);
        }
    };

    const cardStyle = {
        width: '100%',
        maxWidth: 480,
        borderRadius: 16,
        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
    };

    const logoStyle = {
        display: 'block',
        margin: '0 auto 8px',
        width: 160,
    };

    return (
        <AuthLayout>
            <Card style={cardStyle} bodyStyle={{ padding: 24 }}>
                <img src={logo} alt="FUTPREMIA" style={logoStyle} />

                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    <Title level={4}>¿Olvidaste tu contraseña?</Title>
                    <Text type="secondary">
                        Ingresa tu correo y te enviaremos instrucciones para restablecerla.
                    </Text>
                </div>

                {mockEnabled && (
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message="Modo MOCK activado"
                        description="No se enviarán correos reales."
                    />
                )}

                {globalError && (
                    <Alert
                        type="error"
                        showIcon
                        icon={<ExclamationCircleOutlined />}
                        style={{ marginBottom: 16 }}
                        message="Error"
                        description={globalError}
                        closable
                        onClose={() => setGlobalError(null)}
                    />
                )}

                {success ? (
                    <>
                        <Alert
                            type="success"
                            showIcon
                            icon={<MailOutlined />}
                            message="Solicitud enviada"
                            description="Si el correo existe en nuestro sistema, recibirás instrucciones para restablecer tu contraseña."
                            style={{ marginBottom: 16 }}
                        />

                        <Button
                            type="primary"
                            size="large"
                            block
                            onClick={() => navigate('/login')}
                        >
                            Volver a iniciar sesión
                        </Button>
                    </>
                ) : (
                    <Form
                        form={form}
                        name="forgot-password"
                        layout="vertical"
                        requiredMark={false}
                        onFinish={onFinish}
                        validateTrigger={['onBlur', 'onSubmit']}
                    >
                        <Form.Item
                            label="Email"
                            name="email"
                            rules={[
                                { required: true, message: 'El email es obligatorio.' },
                                { type: 'email', message: 'Formato de email inválido.' },
                                { min: 5, message: 'El email debe tener al menos 5 caracteres.' },
                                { max: 100, message: 'El email no puede exceder 100 caracteres.' },
                            ]}
                        >
                            <Input
                                size="large"
                                prefix={<UserOutlined />}
                                placeholder="tu@email.com"
                                autoComplete="email"
                                onChange={handleInputChange}
                            />
                        </Form.Item>

                        <Form.Item style={{ marginTop: 8 }}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                size="large"
                                block
                                loading={loading}
                            >
                                Enviar instrucciones
                            </Button>
                        </Form.Item>

                        <div style={{ textAlign: 'center', marginTop: 8 }}>
                            <button onClick={() => navigate('/login')}>
                                Volver a iniciar sesión
                            </button>
                        </div>
                    </Form>
                )}
            </Card>
        </AuthLayout>
    );
}
